if (!customElements.get('kp-produto')) {
  customElements.define(
    'kp-produto',
    class KpProduto extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-kp-main]');
        this.thumbs = Array.from(this.querySelectorAll('[data-kp-thumb]'));
        this.variantInput = this.querySelector('[data-kp-variant-input]');
        this.quantityInput = this.querySelector('[data-kp-quantity-input]');
        this.radios = Array.from(this.querySelectorAll('.kp-produto__option input'));

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showMedia(thumb)));
        this.radios.forEach((radio) => radio.addEventListener('change', () => this.selectVariant(radio)));
        this.setupKit();
        this.setupSticky();
      }

      setupSticky() {
        this.sticky = this.querySelector('[data-kp-sticky]');
        this.buyButton = this.querySelector('[data-kp-buy], [data-kp-kit-buy]');
        if (!this.sticky || !this.buyButton) return;
        this.stickyBuy = this.sticky.querySelector('[data-kp-sticky-buy]');
        this.stickyLabel = this.sticky.querySelector('[data-kp-sticky-label]');

        let frame;
        const toggle = () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const visible = this.buyButton.getBoundingClientRect().bottom < 0;
            this.sticky.classList.toggle('is-visible', visible);
            this.sticky.inert = !visible;
          });
        };
        window.addEventListener('scroll', toggle, { passive: true });
        window.addEventListener('resize', toggle);
        toggle();

        const sync = () => {
          const loading = this.buyButton.classList.contains('loading');
          this.stickyBuy.classList.toggle('loading', loading);
          this.stickyBuy.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
          this.stickyBuy.disabled = this.buyButton.disabled && !loading;
          const label = this.buyButton.disabled && !loading ? this.stickyBuy.dataset.soldOutLabel : this.stickyBuy.dataset.label;
          if (this.stickyLabel && label) this.stickyLabel.textContent = label;
        };
        new MutationObserver(sync).observe(this.buyButton, { attributes: true, attributeFilter: ['class', 'disabled'] });
        sync();

        this.stickyBuy.addEventListener('click', () => {
          if (this.buyButton.disabled || this.stickyBuy.classList.contains('loading')) return;
          if (this.buyButton.getAttribute('aria-disabled') === 'true') {
            (this.kit || this.buyButton).scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          this.buyButton.click();
        });
      }

      setupKit() {
        this.kit = this.querySelector('[data-kp-kit]');
        this.kitBuy = this.querySelector('[data-kp-kit-buy]');
        if (!this.kit) return;
        this.kitItems = Array.from(this.kit.querySelectorAll('[data-kp-kit-item]'));
        this.kitMin = Number(this.kit.dataset.min) || 1;
        this.kitAllowed = (this.kit.dataset.allowed || '')
          .split(',')
          .map(Number)
          .filter((n) => n > 0)
          .sort((a, b) => a - b);
        this.kitMax = this.kitAllowed.length ? this.kitAllowed[this.kitAllowed.length - 1] : Infinity;
        this.kitItems.forEach((item) => {
          const input = item.querySelector('[data-kp-kit-qty]');
          if (!input) return;
          item.querySelectorAll('[data-kp-kit-step]').forEach((button) =>
            button.addEventListener('click', () => this.setKitQty(item, Number(input.value) + Number(button.dataset.kpKitStep)))
          );
          input.addEventListener('change', () => this.setKitQty(item, input.value));
        });
        if (this.kitBuy) this.kitBuy.addEventListener('click', () => this.addKit());
        this.updateKitTotal();
      }

      setKitQty(item, value) {
        const input = item.querySelector('[data-kp-kit-qty]');
        const others = this.kitCount() - (Number(input.value) || 0);
        const limit = Math.min(Number(input.max) || 99, this.kitMax - others);
        const qty = Math.min(Math.max(parseInt(value, 10) || 0, 0), Math.max(limit, 0));
        input.value = qty;
        item.querySelector('[data-kp-kit-step="-1"]').disabled = qty === 0;
        item.classList.toggle('is-chosen', qty > 0);
        const many = Math.max(qty, 1);
        const price = item.querySelector('[data-kp-kit-row-price]');
        const compare = item.querySelector('[data-kp-kit-row-compare]');
        if (price) price.textContent = this.formatMoney(Number(item.dataset.price) * many);
        if (compare) compare.textContent = this.formatMoney(Number(item.dataset.compare) * many);
        this.showKitError('');
        this.updateKitTotal();
      }

      kitChoices() {
        return this.kitItems
          .map((item) => {
            const input = item.querySelector('[data-kp-kit-qty]');
            return { item, quantity: input ? Number(input.value) || 0 : 0 };
          })
          .filter((choice) => choice.quantity > 0);
      }

      formatMoney(cents) {
        const format = this.kit.dataset.moneyFormat || 'R$ {{amount_with_comma_separator}}';
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const name = match ? match[1] : 'amount';
        const comma = /comma|apostrophe/.test(name) || name === 'amount_with_period_and_space_separator';
        const decimals = /no_decimals/.test(name) ? 0 : 2;
        const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
        const thousands = name.includes('apostrophe') ? "'" : name.includes('space') ? ' ' : comma ? '.' : ',';
        const value = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? (comma ? ',' : '.') + fraction : '');
        return match ? format.replace(match[0], value) : value;
      }

      kitCount() {
        return this.kitChoices().reduce((sum, choice) => sum + choice.quantity, 0);
      }

      kitValid(count) {
        if (this.kitAllowed.length) return this.kitAllowed.includes(count);
        return count >= this.kitMin;
      }

      updateKitButton() {
        if (!this.kitBuy) return;
        const count = this.kitCount();
        this.kitItems.forEach((item) => {
          const plus = item.querySelector('[data-kp-kit-step="1"]');
          if (plus) plus.disabled = count >= this.kitMax;
        });
        if (this.kitBuy.classList.contains('loading')) return;
        const data = this.kitBuy.dataset;
        let text = data.label;
        if (count < this.kitMin) {
          text = data.minLabel;
        } else if (count > this.kitMax) {
          text = (data.maxLabel || '').replace('[n]', this.kitMax);
        } else if (!this.kitValid(count)) {
          const next = this.kitAllowed.find((n) => n > count);
          text = (data.nextLabel || '').replace('[n]', next - count);
        }
        const label = this.kitBuy.querySelector('[data-kp-kit-buy-label]');
        if (label) label.textContent = text;
        if (this.kitValid(count)) this.kitBuy.removeAttribute('aria-disabled');
        else this.kitBuy.setAttribute('aria-disabled', 'true');
      }

      updateKitTotal() {
        let total = 0;
        let compare = 0;
        this.kitChoices().forEach(({ item, quantity }) => {
          total += Number(item.dataset.price) * quantity;
          compare += Number(item.dataset.compare) * quantity;
        });
        this.updateKitButton();
        const totalEl = this.kit.querySelector('[data-kp-kit-total]');
        const compareEl = this.kit.querySelector('[data-kp-kit-compare]');
        const installmentsEl = this.kit.querySelector('[data-kp-kit-installments]');
        if (totalEl) totalEl.textContent = this.formatMoney(total);
        if (compareEl) {
          compareEl.textContent = this.formatMoney(compare);
          compareEl.hidden = compare <= total;
        }
        if (installmentsEl) {
          const count = Number(this.kit.dataset.installments) || 0;
          installmentsEl.style.visibility = count > 1 && total > 0 ? 'visible' : 'hidden';
          const each = this.formatMoney(Math.round(total / count)).replace(/^[^\d]*/, '');
          installmentsEl.textContent = (this.kit.dataset.installmentsLabel || '').replace('[n]', count).replace('[valor]', each);
        }
      }

      showKitError(message) {
        const wrapper = this.querySelector('[data-kp-kit-form] .product-form__error-message-wrapper');
        if (!wrapper) return;
        wrapper.hidden = !message;
        wrapper.querySelector('.product-form__error-message').textContent = message;
      }

      async addKit() {
        if (this.kitBuy.classList.contains('loading')) return;
        const choices = this.kitChoices();
        if (!choices.length || !this.kitValid(this.kitCount())) {
          this.showKitError(this.kitBuy.dataset.empty);
          return;
        }
        const cart = document.querySelector('cart-drawer');
        const sections = cart && cart.getSectionsToRender ? cart.getSectionsToRender().map((section) => section.id) : [];
        const spinner = this.kitBuy.querySelector('.loading__spinner');
        this.kitBuy.classList.add('loading');
        this.kitBuy.setAttribute('aria-disabled', 'true');
        if (spinner) spinner.classList.remove('hidden');
        this.showKitError('');

        try {
          const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: choices.map(({ item, quantity }) => ({ id: Number(item.dataset.variantId), quantity })),
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const state = await response.json();
          if (!response.ok || state.status) {
            this.showKitError(state.description || state.message || (window.cartStrings && window.cartStrings.error) || '');
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kp-kit', cartData: state });
          }
          if (cart && state.sections) {
            cart.classList.remove('is-empty');
            cart.renderContents({ id: state.items && state.items[0] && state.items[0].id, sections: state.sections });
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
          this.kitItems.forEach((item) => item.querySelector('[data-kp-kit-qty]') && this.setKitQty(item, 0));
        } catch (error) {
          console.error(error);
          this.showKitError((window.cartStrings && window.cartStrings.error) || 'Erro ao adicionar ao carrinho.');
        } finally {
          this.kitBuy.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
          this.updateKitButton();
        }
      }

      updatePrice(radio) {
        const price = this.querySelector('[data-kp-price]');
        const compare = this.querySelector('[data-kp-compare]');
        if (price && radio.dataset.price) price.textContent = radio.dataset.price;
        if (compare) {
          compare.textContent = radio.dataset.compare || '';
          compare.hidden = !radio.dataset.compare;
        }
      }

      showMedia(thumb) {
        if (!thumb || !this.main) return;
        const img = this.main.querySelector('img');
        if (img) {
          img.removeAttribute('srcset');
          img.src = thumb.dataset.src;
          img.srcset = thumb.dataset.srcset;
          img.alt = thumb.dataset.alt || '';
        }
        this.thumbs.forEach((t) => {
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
          if (t.parentElement) t.parentElement.hidden = t === thumb;
        });
      }

      selectVariant(radio) {
        if (!radio.checked) return;
        this.updatePrice(radio);
        if (radio.dataset.quantity) {
          if (this.quantityInput) this.quantityInput.value = radio.dataset.quantity;
          return;
        }
        if (!this.variantInput) return;
        this.variantInput.value = radio.value;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', radio.value);
        window.history.replaceState({}, '', url.toString());

        const mediaId = radio.dataset.mediaId;
        if (!mediaId) return;
        this.showMedia(this.thumbs.find((t) => t.dataset.mediaId === mediaId));

        const strip = this.querySelector('[data-kp-strip]');
        const slide = strip && strip.querySelector(`[data-media-id="${mediaId}"]`);
        if (slide && strip.offsetParent) strip.scrollTo({ left: slide.offsetLeft - strip.offsetLeft - 8, behavior: 'smooth' });
      }
    }
  );
}
